import type { Id, Road } from '@pk/shared';

/** Shortest route along roads (by travel days). Returns the holdings to pass through, excluding `from`. */
export function shortestPath(roads: Road[], from: Id, to: Id): { path: Id[]; days: number } | null {
  if (from === to) return { path: [], days: 0 };
  const adj = new Map<Id, { id: Id; days: number }[]>();
  for (const r of roads) {
    if (!adj.has(r.a)) adj.set(r.a, []);
    if (!adj.has(r.b)) adj.set(r.b, []);
    adj.get(r.a)!.push({ id: r.b, days: r.days });
    adj.get(r.b)!.push({ id: r.a, days: r.days });
  }
  const dist = new Map<Id, number>([[from, 0]]);
  const prev = new Map<Id, Id>();
  const open = new Set<Id>([from]);
  while (open.size > 0) {
    let cur: Id | null = null;
    let best = Infinity;
    for (const id of open) {
      const d = dist.get(id)!;
      if (d < best) { best = d; cur = id; }
    }
    if (cur === null) break;
    open.delete(cur);
    if (cur === to) break;
    for (const n of adj.get(cur) ?? []) {
      const nd = best + n.days;
      if (nd < (dist.get(n.id) ?? Infinity)) {
        dist.set(n.id, nd);
        prev.set(n.id, cur);
        open.add(n.id);
      }
    }
  }
  if (!dist.has(to)) return null;
  const path: Id[] = [];
  for (let at: Id | undefined = to; at && at !== from; at = prev.get(at)) path.unshift(at);
  return { path, days: dist.get(to)! };
}

/** Travel days from `from` to every reachable holding (Dijkstra over the road network). */
export function daysFrom(roads: Road[], from: Id): Map<Id, number> {
  const adj = new Map<Id, { id: Id; days: number }[]>();
  for (const r of roads) {
    if (!adj.has(r.a)) adj.set(r.a, []);
    if (!adj.has(r.b)) adj.set(r.b, []);
    adj.get(r.a)!.push({ id: r.b, days: r.days });
    adj.get(r.b)!.push({ id: r.a, days: r.days });
  }
  const dist = new Map<Id, number>([[from, 0]]);
  const open = new Set<Id>([from]);
  while (open.size > 0) {
    let cur: Id | null = null;
    let best = Infinity;
    for (const id of open) { const d = dist.get(id)!; if (d < best) { best = d; cur = id; } }
    if (cur === null) break;
    open.delete(cur);
    for (const n of adj.get(cur) ?? []) {
      if (best + n.days < (dist.get(n.id) ?? Infinity)) { dist.set(n.id, best + n.days); open.add(n.id); }
    }
  }
  return dist;
}
